import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import { useDeliverToAppraisee } from '@/queries/useAppraisalDetail';
import { datePickerLocaleText } from '@/utils/datePickerLocaleText';
import { formatDateTime } from '@/utils/dateFormatter';

// Enable UTC plugin so we can convert the user-picked local datetime to UTC
// before submitting. Backend contract is HH:mm UTC.
dayjs.extend(utcPlugin);

interface DeliverToAppraiseeDialogProps {
  open: boolean;
  onClose: () => void;
  appraisalId: number;
  rowVersion?: string;
  onSuccess: (wasScheduled: boolean) => void;
  onError: (error: unknown) => void;
}

function nextWorkingDay(date: dayjs.Dayjs): dayjs.Dayjs {
  const day = date.day(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
  const daysToAdd = day === 5 ? 3 : day === 6 ? 2 : 1;
  return date.add(daysToAdd, 'day');
}

// "Max 1 working day from now" means today OR the next working day — never the
// weekend in between. Picker disables Sat/Sun (and any other date outside the
// window). Mirrors the backend cap so we don't silently let an invalid date
// reach the API.
function isAllowedDeliveryDate(date: dayjs.Dayjs, today: dayjs.Dayjs): boolean {
  return date.isSame(today, 'day') || date.isSame(nextWorkingDay(today), 'day');
}

export function DeliverToAppraiseeDialog({
  open,
  onClose,
  appraisalId,
  rowVersion,
  onSuccess,
  onError,
}: DeliverToAppraiseeDialogProps) {
  const [mode, setMode] = useState<'immediate' | 'scheduled'>('immediate');
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [selectedTime, setSelectedTime] = useState<dayjs.Dayjs | null>(
    dayjs().hour(8).minute(0).second(0),
  );

  const { mutate, isPending } = useDeliverToAppraisee(String(appraisalId));

  // Reset form state after the close animation finishes so the next open starts clean.
  // Runs after mutation-driven close or user cancel — both go through MUI's Dialog transition.
  const resetFormState = () => {
    setMode('immediate');
    setSelectedDate(null);
    setSelectedTime(dayjs().hour(8).minute(0).second(0));
  };

  const isScheduled = mode === 'scheduled';
  const combined =
    isScheduled && selectedDate && selectedTime
      ? selectedDate.hour(selectedTime.hour()).minute(selectedTime.minute()).second(0)
      : null;
  const isInPast = combined != null && combined.isBefore(dayjs());
  const canDeliver = isScheduled
    ? selectedDate !== null && selectedTime !== null && !isInPast
    : true;

  const handleDeliver = () => {
    if (isScheduled && selectedDate && selectedTime) {
      // Convert the user-picked local datetime to UTC before sending. The backend's
      // DeliverToAppraiseeRequestDto.DeliveryTime is documented as "HH:mm UTC" — without
      // this conversion, a user in CEST picking 14:00 would have delivery scheduled for
      // 14:00 UTC = 16:00 CEST. Combining date+time first then .utc() gives us the correct
      // wall-clock moment expressed as a UTC date+time pair.
      const localCombined = selectedDate
        .hour(selectedTime.hour())
        .minute(selectedTime.minute())
        .second(0)
        .millisecond(0);
      const asUtc = localCombined.utc();
      mutate(
        {
          rowVersion,
          deliveryDate: asUtc.format('YYYY-MM-DD'),
          deliveryTime: asUtc.format('HH:mm'),
        },
        {
          onSuccess: () => {
            onSuccess(true);
          },
          onError: (error) => {
            onError(error);
          },
        },
      );
    } else {
      mutate(
        { rowVersion },
        {
          onSuccess: () => {
            onSuccess(false);
          },
          onError: (error) => {
            onError(error);
          },
        },
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited: resetFormState }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Deliver to appraisee</DialogTitle>
      <DialogContent>
        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value as 'immediate' | 'scheduled')}
        >
          <FormControlLabel
            value="immediate"
            control={<Radio />}
            label="Deliver immediately"
            sx={{
              bgcolor: mode === 'immediate' ? 'action.selected' : 'transparent',
              borderRadius: 1,
              mx: 0,
              px: 1,
            }}
          />
          <FormControlLabel
            value="scheduled"
            control={<Radio />}
            label="Schedule delivery"
            sx={{
              bgcolor: mode === 'scheduled' ? 'action.selected' : 'transparent',
              borderRadius: 1,
              mx: 0,
              px: 1,
            }}
          />
        </RadioGroup>

        {isScheduled && (
          <LocalizationProvider dateAdapter={AdapterDayjs} localeText={datePickerLocaleText}>
            <Box sx={{ mt: 2 }}>
              <DatePicker
                label="Delivery date"
                value={selectedDate}
                onChange={(value: dayjs.Dayjs | null) => setSelectedDate(value)}
                minDate={dayjs()}
                maxDate={nextWorkingDay(dayjs.utc())}
                shouldDisableDate={(d: dayjs.Dayjs) => !isAllowedDeliveryDate(d, dayjs.utc())}
                format="DD MMM YYYY"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <TimePicker
                label="Delivery time"
                value={selectedTime}
                onChange={(value: dayjs.Dayjs | null) => setSelectedTime(value)}
                format="HH:mm"
                ampm={false}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: { mt: 2 } } }}
              />
            </Box>
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Choose a date and time (max. 1 working day from now).
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Delivery will be sent at the selected time in your local timezone.
            </Typography>
            {isInPast && (
              <Typography variant="body2" sx={{ mt: 1, color: 'error.main' }}>
                Time must be in the future.
              </Typography>
            )}
            {combined && !isInPast && (
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Delivery scheduled for: {formatDateTime(combined.toDate())}
              </Typography>
            )}
          </LocalizationProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDeliver}
          disabled={isPending || !canDeliver}
        >
          Deliver
        </Button>
      </DialogActions>
    </Dialog>
  );
}
